/* eslint-disable no-lone-blocks */

import { forwardRef } from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center w-fit disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 whitespace-nowrap',
  {
    variants: {
      variant: {
        magic: 'group/magic relative inline-flex h-12 overflow-hidden rounded-lg w-full p-[1px] md:w-60 md:mt-10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple',
        simple: 'px-4 py-2 rounded-md border border-neutral-300 bg-neutral-100 text-neutral-500 text-sm hover:-translate-y-1 transform transition duration-200 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple',
      },
    },
    defaultVariants: {
      variant: 'simple',
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariantProps {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'simple', asChild = false, children, ...props }, ref) => {
    const Component = asChild ? Slot : 'button';

    const commonProps = {
      ref,
      className: cn(buttonVariants({ variant, className })),
      ...props,
    };

    switch (variant) {
      case 'magic':
        return (
          <Component {...commonProps}>
            <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]" />
            <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-lg bg-slate-950 px-7 text-sm font-medium text-white backdrop-blur-3xl transition-colors duration-200 group-hover/magic:bg-slate-900">
              {children}
            </span>
          </Component>
        );
      default:
        return (
          <Component {...commonProps}>
            {children}
          </Component>
        );
    }
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
